import { Component, OnDestroy, OnInit } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { RouterLink } from "@angular/router";
import { CommonModule } from "@angular/common";
import { FontAwesomeModule } from "@fortawesome/angular-fontawesome";
import { faPencil } from "@fortawesome/free-solid-svg-icons";
import { Subscription } from "rxjs";
import { FileListComponent } from "../file-list/file-list.component";
import { SubmissionService, SubmissionStatus } from "../services/submission.service";
import { Survey, SurveyService } from "../services/survey.service";
import { SurveyComponent } from "../survey/survey.component";
import { FileDropzoneComponent } from "../file-dropzone/file-dropzone.component";
import { ConfirmSubmissionComponent } from "../confirm-submission/confirm-submission.component";
import { LanguageService } from "../services/language.service";
import { SurveyQuestionComponent } from "../survey-question/survey-question.component";

type Breadcrumb = {
    status: Exclude<SubmissionStatus, 'survey'>,
    text: string
} | {
    status: 'survey',
    text: string,
    surveyType: Survey['type']
};

@Component({
    selector: 'ell-submission',
    standalone: true,
    imports: [CommonModule, FormsModule, FileListComponent, FileDropzoneComponent, SurveyComponent, RouterLink, FontAwesomeModule, ConfirmSubmissionComponent, SurveyQuestionComponent],
    templateUrl: './submission.component.html',
    styleUrl: './submission.component.scss'
})
export class SubmissionComponent implements OnInit, OnDestroy {
    public faPencil = faPencil;
    public files: File[] = [];

    /**
     * Survey to fill in
     */
    public survey?: Survey;
    public status$ = this.submissionService.status$;

    /**
     * Counting down until actual submission start
     * To give people time to abort
     */
    public countingDown = false;

    public showBreadcrumb = false;
    public breadcrumb: Breadcrumb[] = [{
        status: 'ready',
        text: $localize`Add Files`
    }, {
        status: 'survey',
        text: $localize`Agreement Form`,
        surveyType: 'consent'
    }, {
        status: 'survey',
        text: $localize`Email Form`,
        surveyType: 'email'
    }, /* {
        status: 'survey',
        text: $localize`Information about the Writer`,
        surveyType: 'writer'
    }, {
        status: 'survey',
        text: $localize`Information about the Letter(s)`,
        surveyType: 'file'
    }, */ {
        status: 'review',
        text: $localize`Review`
    }, {
        status: 'complete',
        text: $localize`Complete`
    }];

    yesNo = [$localize`Yes`, $localize`No`];
    uploadMore = '';

    public activeBreadcrumb = -1;

    private subscription: Subscription;

    get email() {
        return this.surveyService.email;
    }

    constructor(private submissionService: SubmissionService,
        languageService: LanguageService,
        private surveyService: SurveyService
    ) {
        this.subscription =
            surveyService.incompleteSurveys$.subscribe(
                surveys => {
                    if (surveys.length) {
                        this.survey = surveys[0];
                        this.showBreadcrumb = true;
                        this.activeBreadcrumb = this.breadcrumb.findIndex(crumb =>
                            crumb.status === 'survey' && crumb.surveyType === this.survey!.type);
                    }
                }
            );
        this.subscription.add(
            submissionService.status$.subscribe(status => {
                if (status === 'survey') {
                    // ask again for more at the end
                    this.uploadMore = '';
                    // let the survey listener handle that
                    return;
                }

                this.activeBreadcrumb = this.breadcrumb.findIndex(crumb => crumb.status === status);
                if (this.activeBreadcrumb > 0) {
                    this.showBreadcrumb = true;
                }
            })
        );

        this.subscription.add(this.submissionService.files$.subscribe(files => {
            // once we have some files don't allow us to toggle,
            // to prevent losing those files!
            languageService.disableLanguageToggle(files.length > 0);
            this.files = files
        }));
    }

    ngOnInit(): void {
        this.submissionService.start();
    }

    ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }

    addFiles(files: File[]) {
        this.submissionService.addFiles(files);
    }

    deleteFile(file: File) {
        this.submissionService.deleteFile(file);
    }

    submit() {
        this.submissionService.submit(this.files);
    }

    editEmail() {
        this.surveyService.reopenSurvey(0);
        this.submissionService.setStatus('survey');
    }

    encryptionActivated() {
        return this.submissionService.encryptionActivated();
    }
}
